import { css, styled } from 'styled-components'
import { Link } from 'react-router'

export type GlassButtonVariant = 'primary' | 'glass' | 'dark'
export type GlassButtonSize = 'small' | 'medium' | 'large'

const sizeStyles: Record<GlassButtonSize, ReturnType<typeof css>> = {
  small: css`
    height: 36px;
    min-width: 106px;
    padding: 0 18px;
    border-radius: 14px;
  `,
  medium: css`
    height: 44px;
    padding: 0 18px;
    border-radius: 17px;
  `,
  large: css`
    height: 48px;
    padding: 0 28px;
    border-radius: 18px;
  `,
}

const labelFontSize: Record<GlassButtonSize, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
}

const tintBackground: Record<GlassButtonVariant, string> = {
  primary: 'rgba(235, 35, 113, 1)',
  glass: 'rgba(255, 255, 255, 0.5)',
  dark: 'rgba(43, 5, 20, 1)',
}

const textColor: Record<GlassButtonVariant, string> = {
  primary: '#ffffff',
  glass: '#2b0514',
  dark: '#ffffff',
}

export const Effect = styled.span`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
`

export const Tint = styled.span<{ $variant: GlassButtonVariant }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  background: ${({ $variant }) => tintBackground[$variant]};
`

export const Shine = styled.span`
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  box-shadow: inset 1px 1px 1px 0 rgba(255, 255, 255, 0.9);
  transition: box-shadow 0.2s ease;
`

// Stacked layers: effect (backdrop blur + SVG displacement) → tint → shine → text.
const wrapperStyles = css<{ $size: GlassButtonSize }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  isolation: isolate;
  overflow: hidden;
  border: none;
  background: transparent;
  cursor: pointer;
  outline: none;
  text-decoration: none;
  transition:
    transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.6),
    box-shadow 0.2s ease;

  ${({ $size }) => sizeStyles[$size]}

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 3;
    border-radius: inherit;
    background: transparent;
    transition: background 0.15s ease;
    pointer-events: none;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-1px) scale(1.02);
      box-shadow: 0 6px 16px rgba(43, 5, 20, 0.16);
    }

    &:hover ${Shine} {
      box-shadow:
        inset 1px 1px 1px 0 rgba(255, 255, 255, 1),
        inset 0 0 8px 0 rgba(255, 255, 255, 0.35);
    }
  }

  &:active {
    transform: scale(0.96);
  }

  &:active::after {
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 3px solid rgba(235, 35, 113, 0.45);
    outline-offset: 2px;
  }
`

export const Wrapper = styled.button<{ $size: GlassButtonSize }>`
  ${wrapperStyles}
`

export const LinkWrapper = styled(Link)<{ $size: GlassButtonSize }>`
  ${wrapperStyles}
`

export const Text = styled.span<{
  $variant: GlassButtonVariant
  $size: GlassButtonSize
}>`
  position: relative;
  z-index: 4;
  font-weight: 600;
  letter-spacing: -0.0071em;
  white-space: nowrap;
  font-size: ${({ $size }) => labelFontSize[$size]};
  color: ${({ $variant }) => textColor[$variant]};
`

export const Filter = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
`
